import React, { useEffect, useRef } from "react";
import { services } from "../../mock/mock";
import ServiceCard from "./ServiceCard";

/**
 * Draggable / swipeable service cards with a gentle continuous auto-scroll.
 * - Desktop: click-drag to scroll, auto-scrolls and pauses on hover.
 * - Mobile: native touch swipe (auto-scroll pauses while touching).
 * Cards are equal height and the row loops seamlessly (3 duplicated segments).
 */
const ServiceMarquee = () => {
  const scroller = useRef(null);
  const raf = useRef(0);
  const paused = useRef(false);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });
  const row = [...services, ...services, ...services];

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const seg = () => el.scrollWidth / 3;
    el.scrollLeft = seg(); // start in the middle copy

    const tick = () => {
      if (el && !paused.current && !drag.current.active) {
        el.scrollLeft += 0.55;
        if (el.scrollLeft >= seg() * 2) el.scrollLeft -= seg();
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  // keep manual scrolling within the looping range
  const onScroll = () => {
    const el = scroller.current;
    if (!el || drag.current.active) return;
    const seg = el.scrollWidth / 3;
    if (el.scrollLeft < seg * 0.5) el.scrollLeft += seg;
    else if (el.scrollLeft > seg * 2.5) el.scrollLeft -= seg;
  };

  const onMouseDown = (e) => {
    const el = scroller.current;
    if (!el) return;
    drag.current = { active: true, startX: e.pageX, startScroll: el.scrollLeft, moved: false };
    el.classList.add("cursor-grabbing");
  };
  const onMouseMove = (e) => {
    const el = scroller.current;
    if (!el || !drag.current.active) return;
    e.preventDefault();
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = () => {
    const el = scroller.current;
    drag.current.active = false;
    if (el) el.classList.remove("cursor-grabbing");
  };
  // prevent a card from navigating if the user was dragging
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div className="relative">
      <div
        ref={scroller}
        className="flex gap-5 overflow-x-auto no-scrollbar cursor-grab select-none py-2"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => {
          paused.current = false;
          endDrag();
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onClickCapture={onClickCapture}
        onTouchStart={() => (paused.current = true)}
        onTouchEnd={() => (paused.current = false)}
        onScroll={onScroll}
      >
        {row.map((s, i) => (
          <div
            key={`${s.slug}-${i}`}
            className="w-[270px] sm:w-[300px] flex-shrink-0"
          >
            <ServiceCard service={s} />
          </div>
        ))}
      </div>

      {/* soft white fog fades on each end */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-14 sm:w-24 z-10"
        style={{ background: "linear-gradient(to right, #ffffff, rgba(255,255,255,0))" }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-14 sm:w-24 z-10"
        style={{ background: "linear-gradient(to left, #ffffff, rgba(255,255,255,0))" }}
      />
    </div>
  );
};

export default ServiceMarquee;
